import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { updatePersonalInfo } from '../services/auth';
import useFieldValidation from './useFieldValidation';
import UserMobileField from '../pages/users/UserMobileField';
import { parseMobile, composeMobile } from './CustomerModal';
import '../pages/warehouses/warehouses.css';

function validatePersonalInfo(values) {
  const errors = {};
  const name = values.name.trim();
  const email = values.email.trim();
  const mobile = values.mobile.trim();
  if (!name) errors.name = 'Name is required.';
  else if (name.length > 150) errors.name = 'Name must not be greater than 150 characters.';
  if (!email) errors.email = 'Email is required.';
  else if (email.length > 255 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address.';
  const nationalMobile = mobile.replace(/^\+\d{1,4}\s+/, '');
  if (mobile && (mobile.length > 50 || !/^\+?[0-9 ()-]{7,50}$/.test(mobile))) errors.mobile = 'Enter a valid mobile number.';
  else if (nationalMobile.replace(/\D/g, '').length > 10) errors.mobile = 'Mobile number must not be greater than 10 digits.';
  return errors;
}

export default function PersonalInfoModal({ user, onClose, onSaved }) {
  const [values, setValues] = useState({ name: user?.name || '', email: user?.email || '', mobile: user?.mobile || '' });
  const [dialIso, setDialIso] = useState(() => parseMobile(user?.mobile).iso);
  const [nationalNumber, setNationalNumber] = useState(() => parseMobile(user?.mobile).national.replace(/\D/g, ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { errors, setErrors, touchField } = useFieldValidation(values, validatePersonalInfo);
  const dialog = useRef(null);
  const pending = useRef(false);
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current?.querySelector('input')?.focus();
    function key(event) {
      if (event.key === 'Escape' && !pending.current) onClose();
      if (event.key === 'Tab') {
        const items = [...dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
        const first = items[0]; const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('keydown', key); previous?.focus(); };
  }, [onClose]);
  async function submit(event) {
    event.preventDefault();
    if (pending.current) return;
    const validation = validatePersonalInfo(values);
    setErrors(validation);
    if (Object.keys(validation).length) {
      dialog.current?.querySelector(`#personal-info-${Object.keys(validation)[0]}`)?.focus();
      return;
    }
    pending.current = true; setBusy(true); setError('');
    try { await updatePersonalInfo({ name: values.name.trim(), email: values.email.trim().toLowerCase(), mobile: values.mobile.trim() }); onSaved(); }
    catch (err) { setError(err.message); }
    finally { pending.current = false; setBusy(false); }
  }
  return <div className="app-password-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !pending.current) onClose(); }}>
    <form className="app-password-modal" role="dialog" aria-modal="true" aria-labelledby="personal-info-title" ref={dialog} onSubmit={submit} noValidate>
      <header className="app-password-heading"><h2 id="personal-info-title">Change Personal Info</h2><button type="button" aria-label="Close personal info" disabled={busy} onClick={onClose}><X size={18} /></button></header>
      <p className="app-password-help">Update your account details</p>
      {['name', 'email'].map(field => <div className="app-password-field" key={field}>
        <label htmlFor={`personal-info-${field}`}>{field === 'name' ? 'Name' : field === 'email' ? 'Email' : 'Mobile'}</label>
        <div className={`app-password-input${errors[field] ? ' invalid' : ''}`}>
          <input id={`personal-info-${field}`} aria-invalid={Boolean(errors[field])} aria-describedby={errors[field] ? `personal-info-${field}-error` : undefined} onBlur={() => touchField(field)} type={field === 'email' ? 'email' : field === 'mobile' ? 'tel' : 'text'} required={field !== 'mobile'} placeholder={field === 'mobile' ? 'Enter mobile number' : undefined} maxLength={field === 'name' ? 150 : field === 'email' ? 255 : 50} disabled={busy} value={values[field]} onChange={event => { setError(''); setValues(current => ({ ...current, [field]: event.target.value })); }} />
        </div>
        {errors[field] && <p id={`personal-info-${field}-error`} className="app-password-error" role="alert">{errors[field]}</p>}
      </div>)}
      <div className="app-password-field"><UserMobileField id="personal-info-mobile" value={nationalNumber} iso={dialIso} disabled={busy} error={errors.mobile} onBlur={() => touchField('mobile')} onChange={number => { setNationalNumber(number); setError(''); setValues(current => ({ ...current, mobile: composeMobile(dialIso, number) })); }} onCountryChange={iso => { setDialIso(iso); setError(''); setValues(current => ({ ...current, mobile: composeMobile(iso, nationalNumber) })); }} /></div>
      {error && <p role="alert" className="app-password-error">{error}</p>}
      <div className="app-password-actions"><button type="button" disabled={busy} onClick={onClose}>Cancel</button><button type="submit" disabled={busy || Object.keys(validatePersonalInfo(values)).length > 0}>{busy ? 'Saving...' : 'Save Changes'}</button></div>
    </form>
  </div>;
}
